/* eslint-disable jsx-a11y/img-redundant-alt */
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Helmet } from 'react-helmet';
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import car from "../../src/images/service/car.png";
import Navbar from "../components/navbar";
import Footer from "../homeComponents/footer";
import toast from "react-hot-toast";

const Service = () => {
  const location = useLocation();
  const [selectedButton, setSelectedButton] = useState(""); // Button state
  const [inspectionType, setInspectionType] = useState(""); // Inspection type state
  const [vehicleType, setVehicleType] = useState("Luxury");
  const [carImage, setCarImage] = useState("");
  const [formData, setFormData] = useState({
    customerName: "",
    customerPhone: "",
    vehicleModel: "",
    vehicleMake: "",
    variant: "",
    address: "",
    allottedVin: "",
    deliveryDate: "",
    dealerType: "",
    customMessage: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  // Extract the query parameter
  useEffect(() => {
    // Scroll to the top of the page on component mount
    window.scrollTo(0, 0);
  
    // Extract the query parameter for inspectionType
    const params = new URLSearchParams(location.search);
    const inspectionTypeParam = params.get("inspectionType");
    // console.log("Decoded inspectionTypeParam:", inspectionTypeParam); // Debug
    if (inspectionTypeParam) {
      setInspectionType(inspectionTypeParam);
    }
  }, []); // Empty dependency array ensures this runs only once  

  // Amounts for each inspection type
  const inspectionAmounts = {
    "Pre Delivery Inspection": {
      Luxury: 7999,
      Ordinary: 1999,
    },
    "Used Car Inspections": {
      Luxury: 2499,
      Ordinary: 1499,
    },
    "Pre Trip Inspections": {
      Luxury: 999,
      Ordinary: 499,
    },
  };

  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user); // Get the entire user object
  const userId = user ? user.id : null; // Safely access user.id
  const navigate = useNavigate();

  // Function to fetch car image when vehicle model changes
  const fetchCarImage = async (vehicleModel) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/cars/image?model=${vehicleModel}`
      );
      // console.log("Backend response:", response.data); // Log the response data to see the structure

      if (
        response.data &&
        response.data.images &&
        response.data.images.length > 0
      ) {
        // Filter images to find only PNG images by checking mime type
        const pngImages = response.data.images.filter(
          (image) => image.mime === "image/png"
        );

        if (pngImages.length > 0) {
          const firstImage = pngImages[0]; // Get the first PNG image
          setCarImage(firstImage.link); // Assuming 'link' is the field holding the image URL
        } else {
          console.error("No PNG images found in the response.");
        }
      } else {
        console.error("No images found in the response.");
      }
    } catch (error) {
      console.error("Error fetching car image: ", error);
    }
  };

  useEffect(() => {
    if (userId) {
      const fetchUserPhoneNumber = async () => {
        try {
          const response = await axios.get(
            `${API_URL}/api/users/get-phone/${userId}`
          );
          const userPhone = response.data.phoneNumber; // Assuming the field is phoneNumber

          if (userPhone) {
            // If phone number exists, set it in formData
            setFormData((prevData) => ({
              ...prevData,
              customerPhone: userPhone,
            }));
          } else {
            // If no phone number exists, do nothing for now
          }
        } catch (error) {
          console.error("Error fetching user phone number:", error);
        }
      };

      fetchUserPhoneNumber();
    }
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Fetch car image when vehicle model changes
    // if (name === "vehicleModel") {
    //   fetchCarImage(value);
    // }
  };

  const handleButtonClick = (buttonName) => {
    setSelectedButton(buttonName); // Update selected button
    // Set the dealerType based on the selected button
    setFormData((prev) => ({
      ...prev,
      dealerType: buttonName === "dealerShowroom" ? "Showroom" : "Stockyard",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Validate required fields
    const requiredFields = [
      "customerName",
      "customerPhone",
      "vehicleMake",
      "vehicleModel",
      "variant",
      "address",
      "deliveryDate",
    ];

    const missingField = requiredFields.find(field => !formData[field]);
    if (missingField) {
      toast.error("Please fill in all required fields.");
      setIsLoading(false);
      return;
    }

    // Validate phone number
    const phonePattern = /^[0-9]{10}$/;
    if (!phonePattern.test(formData.customerPhone)) {
      toast.error("Invalid phone number. Enter a valid 10-digit number.");
      setIsLoading(false);
      return;
    }

    let existingPhoneNumber = null;

    // Try fetching existing phone
    if (userId) {
      try {
        const response = await axios.get(`${API_URL}/api/users/get-phone/${userId}`);
        existingPhoneNumber = response?.data?.phoneNumber || null;
      } catch (err) {
        if (err.response?.status === 404) {
          console.warn("User not found. Proceeding without phone check.");
        } else {
          console.error("Phone number fetch error:", err);
          toast.error("Error checking existing phone number.");
          setIsLoading(false);
          return;
        }
      }
    }

    // Save new phone number if different
    try {
      if (formData.customerPhone && formData.customerPhone !== existingPhoneNumber) {
        await axios.post(`${API_URL}/api/users/save-phone`, {
          userId,
          phoneNumber: formData.customerPhone,
        });
      }
    } catch (error) {
      console.error("Phone save error:", error);
      toast.error("Error saving phone number.");
      setIsLoading(false);
      return;
    }

    // Try creating the service
    try {
      const serviceResponse = await axios.post(`${API_URL}/api/services/create`, {
        inspectionType,
        vehicleType: vehicleType || "",
        userId,
        ...formData,
      });

      if (serviceResponse.status === 200) {
        toast.success("Service created successfully!");
        navigate("/progress", {
          state: { inspectionType, userId, ...formData },
        });
      } else {
        toast.error("Service creation failed.");
      }
    } catch (error) {
      console.error("Service creation error:", error);
      toast.error("Error in service creation: " + (error?.response?.data?.message || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-banner w-full min-h-screen font-clash">
            <Helmet>
              <title>Service</title>
              <meta
                name="keywords"
                content="Pdi, Bengaluru, Bangalore, Kardo, Inspection, Ze, Pre delivery, Services, Car, Cars, Vaidya, BHP, Team, CID, Expert, In, Best, 24, BOSS, Dail, Just, New car, Checklist, Detailed, Report, Inspect, Reliable, Blog, Used car inspection, Buying a new car"
              />
              <meta
                name="description"
                content="Explore our reliable pre-delivery inspection services for new and used cars. Detailed reports, expert insights, and the best service in Bangalore and beyond."
              />
            </Helmet>      
      <Navbar />
      {/* Check for authentication and userId */}
      {!isAuthenticated || !userId ? (
        <div className="flex items-center justify-center text-center bg-banner min-h-screen text-[#FFFF]">Please login to continue</div>
      ) : (
        <div className="flex items-center justify-center lg:pt-10">
          <div className="lg:max-w-lg  mx-auto p-4">
            <h1 className="text-[42px] text-[#FFFF] font-semibold mb-10 pt-28">
              <span>Hey, {user ? user.firstname : "User"}</span>
            </h1>
            <select
              value={inspectionType}
              onChange={(e) => setInspectionType(e.target.value)}
              className="block lg:w-full p-2 border border-none rounded bg-[#1F1F1F] text-[#F0FFC5]"
            >
              <option value="">Select an inspection</option>
              <option value="Pre Delivery Inspection">
                Pre Delivery Inspection
              </option>
              <option value="Used Car Inspections">Used Car Inspections</option>
              <option value="Pre Trip Inspections">Pre Trip Inspections</option>
            </select>

            <form onSubmit={handleSubmit} className="space-y-4 pt-20 pb-32">
              {inspectionType === "Pre Delivery Inspection" && (
               <div className="flex flex-col sm:flex-row items-center justify-center pb-10 gap-2 max-w-[300px] mx-auto">
               {/* Luxury Button */}
               <div
                 onClick={() => setVehicleType("Luxury")}
                 className={`text-center font-semibold cursor-pointer w-full ${
                   vehicleType === "Luxury" ? "text-[#ADD144]" : "text-black"
                 }`}
               >
                 <p
                   className={`px-12 py-2  ${
                     vehicleType === "Luxury"
                       ? "bg-[#ADD144] text-black"
                       : "bg-transparent text-[#CBCBCB]"
                   }`}
                 >
                   Luxury
                 </p>
               </div>
             
               {/* Ordinary Button */}
               <div
                 onClick={() => setVehicleType("Ordinary")}
                 className={`text-center font-semibold cursor-pointer w-full ${
                   vehicleType === "Ordinary" ? "text-[#ADD144]" : "text-black"
                 }`}
               >
                 <p
                   className={`px-12 py-2  ${
                     vehicleType === "Ordinary"
                       ? "bg-[#ADD144] text-black"
                       : "bg-transparent text-[#CBCBCB]"
                   }`}
                 >
                   Ordinary
                 </p>
               </div>
             </div>
             
              )}

              <div className="relative mb-8 lg:w-[480px] w-[300px]">
                <label className="absolute left-6 -top-3 bg-banner bg-opacity-70 text-[#F0FFC5] font-semibold px-1">
                  Customer Name
                </label>
                <input
                  type="text"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleChange}
                  required
                  className="border rounded-3xl w-full py-3 px-3 bg-transparent border-[#F0FFC5] focus:outline-none focus:ring-2 focus:ring-[#F0FFC5] transition text-[#F0FFC5]"
                />
              </div>
              <div className="relative mb-8 lg:w-[480px] w-[300px] pt-3">
                <label className="absolute left-5 lg:top-0 top-0 bg-banner bg-opacity-70 text-[#F0FFC5] font-semibold px-1">
                  Customer Phone
                </label>
                <input
                  type="text"
                  name="customerPhone"
                  value={formData.customerPhone}
                  onChange={handleChange}
                  required
                  className="border rounded-3xl w-full py-3 px-3 bg-transparent border-[#F0FFC5] focus:outline-none focus:ring-2 focus:ring-[#F0FFC5] transition text-[#F0FFC5]"
                />
              </div>
              <div className="flex flex-col space-y-4 pt-8">
                {/* Vehicle Details */}

                {/* Vehicle Make */}
                <div className="flex flex-col space-y-4 pt-2">
  {/* Vehicle Details */}

  <div className="flex flex-col md:flex-row md:space-x-4 w-full">
    {/* Vehicle Make */}
    <div className="relative mb-6 md:mb-0 w-full md:w-[500px] lg:w-[700px] xl:w-[800px] max-w-full">
      <label className="absolute left-4 top-[-12px] bg-banner bg-opacity-70 text-[#F0FFC5] font-semibold px-1">
        Vehicle Make
      </label>
      <input
        type="text"
        name="vehicleMake"
        value={formData.vehicleMake}
        onChange={handleChange}
        required
        className="border rounded-3xl w-full py-3 px-3 bg-transparent border-[#F0FFC5] focus:outline-none focus:ring-2 focus:ring-[#F0FFC5] transition text-[#F0FFC5]"
      />
    </div>

    {/* Vehicle Model */}
    <div className="relative mb-6 md:mb-0 w-full md:w-[500px] lg:w-[700px] xl:w-[800px] max-w-full">
      <label className="absolute left-4 top-[-12px] bg-banner bg-opacity-70 text-[#F0FFC5] font-semibold px-1">
        Vehicle Model
      </label>
      <input
        type="text"
        name="vehicleModel"
        value={formData.vehicleModel}
        onChange={handleChange}
        required
        className="border rounded-3xl w-full py-3 px-3 bg-transparent border-[#F0FFC5] focus:outline-none focus:ring-2 focus:ring-[#F0FFC5] transition text-[#F0FFC5]"
      />
    </div>

    {/* Variant */}
    <div className="relative mb-6 w-full md:w-[500px] lg:w-[700px] xl:w-[800px] max-w-full">
      <label className="absolute left-4 top-[-12px] bg-banner bg-opacity-70 text-[#F0FFC5] font-semibold px-1">
        Variant
      </label>
      <input
        type="text"
        name="variant"
        value={formData.variant}
        onChange={handleChange}
        required
        className="border rounded-3xl w-full py-3 px-3 bg-transparent border-[#F0FFC5] focus:outline-none focus:ring-2 focus:ring-[#F0FFC5] transition text-[#F0FFC5]"
      />
    </div>
  </div>
</div>


           {/* Inspection Type Buttons */}
{inspectionType === "Pre Delivery Inspection" && (
  <div className="flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0 w-full pb-6"> {/* Added pb-6 for bottom padding */}
    {/* Dealer Showroom */}
    <button
      type="button"
      onClick={() => handleButtonClick("dealerShowroom")}
      className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-full transition-all duration-300 ${
        selectedButton === "dealerShowroom"
          ? "bg-[#191F09] text-[#ADD144] font-semibold"
          : "bg-transparent border border-white text-white font-semibold"
      }`}
    >
      <span
        className={`relative flex items-center justify-center w-5 h-5 border-2 border-white rounded-full transition-colors duration-300 ${
          selectedButton === "dealerShowroom" ? "bg-[#ADD144]" : "bg-transparent"
        }`}
      >
        {selectedButton === "dealerShowroom" && (
          <span className="absolute text-[#191F09] text-lg">&#10003;</span>
        )}
      </span>
      <span>Dealer Showroom</span>
    </button>

    {/* Dealer Stockyard */}
    <button
      type="button"
      onClick={() => handleButtonClick("dealerStockyard")}
      className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-full transition-all duration-300 ${
        selectedButton === "dealerStockyard"
          ? "bg-[#191F09] text-[#ADD144] font-semibold"
          : "bg-transparent border border-white text-white font-semibold"
      }`}
    >
      <span
        className={`relative flex items-center justify-center w-5 h-5 border-2 border-white rounded-full transition-colors duration-300 ${
          selectedButton === "dealerStockyard" ? "bg-[#ADD144]" : "bg-transparent"
        }`}
      >
        {selectedButton === "dealerStockyard" && (
          <span className="absolute text-[#191F09] text-lg">&#10003;</span>
        )}
      </span>
      <span>Dealer Stockyard</span>
    </button>
  </div>
)}


                {/* Address */}
                <div className="relative mb-8 lg:w-[480px] w-[300px]">
                  <label className="absolute left-4 top-[-12px] bg-banner bg-opacity-70 text-[#F0FFC5] font-semibold px-1">
                    Address
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    className="border rounded-3xl w-full py-3 px-3 bg-transparent border-[#F0FFC5] focus:outline-none focus:ring-2 focus:ring-[#F0FFC5] transition text-[#F0FFC5]"
                  />
                </div>
              </div>
              <div className="flex flex-col md:flex-row md:space-x-4 w-full">
                {/* Allotted VIN */}
                <div className="relative mb-8 mt-5 lg:w-full w-[300px]">
                  <label className="absolute left-6 -top-3 bg-banner bg-opacity-70 text-[#F0FFC5] font-semibold px-1">
                    Allotted VIN
                  </label>
                  <input
                    type="text"
                    name="allottedVin"
                    value={formData.allottedVin}
                    onChange={handleChange}
                    className="border rounded-3xl lg:w-full w-[300px] py-3 px-3 bg-transparent border-[#F0FFC5] focus:outline-none focus:ring-2 focus:ring-[#F0FFC5] transition text-[#F0FFC5]"
                  />
                </div>

                {/* Inspection Date */}
                <div className="relative mb-8 mt-5 lg:w-full w-[300px]">
  <label className="absolute left-6 -top-3 bg-banner bg-opacity-70 text-[#F0FFC5] font-semibold px-1">
    Inspection Date
  </label>
  <input
    type="date"
    name="deliveryDate"
    value={formData.deliveryDate}
    onChange={handleChange}
    required
    className="border rounded-3xl lg:w-full w-[300px] py-3 px-3 bg-transparent border-[#F0FFC5] focus:outline-none focus:ring-2 focus:ring-[#F0FFC5] transition text-[#F0FFC5] appearance-none"
  />
  <style jsx>
    {`
      input[type="date"]::-webkit-calendar-picker-indicator {
        filter: invert(77%) sepia(18%) saturate(422%) hue-rotate(74deg)
          brightness(96%) contrast(85%);
        cursor: pointer;
      }

      input[type="date"]::-webkit-datetime-edit,
      input[type="date"]::-webkit-inner-spin-button,
      input[type="date"]::-webkit-clear-button {
        color: #F0FFC5;
      }
    `}
  </style>
</div>

              </div>

              <div className="relative mb-8 lg:w-[480px] w-[300px]">
                <label className="absolute left-6 top-5 bg-banner bg-opacity-70 text-[#F0FFC5] font-semibold px-1">
                  Message
                </label>
                <input
                  type="text"
                  name="customMessage"
                  value={formData.customMessage}
                  onChange={handleChange}
                  className="border rounded-3xl w-full py-3 px-3 bg-transparent mt-8 border-[#F0FFC5] focus:outline-none focus:ring-2 focus:ring-[#F0FFC5] transition text-[#F0FFC5]"
                />
              </div>

              {inspectionType && vehicleType && (
                <div className="text-[#ADD143] font-semibold mb-4">
                  Amount to be paid: Rs{" "}
                  {inspectionAmounts[inspectionType] && inspectionAmounts[inspectionType][vehicleType] || 0}
                </div>
              )}

              <div className="flex items-center w-full lg:ml-20 ml-0 justify-center mt-4 ">
                <button
                  type="submit"
                  className="w-full max-w-[200px] p-2 bg-transparent border rounded-full border-[#ADD143] text-[#F0FFC5] font-semibold transition lg:mt-0 mt-10"
                  disabled={isLoading}
                >
                  {isLoading ? "Registering..." : "Register"}
                </button>
              </div>
            </form>
          </div>

          {/* Right Section */}
          <div className=" items-center justify-center lg:flex hidden">
            <img
              src={carImage || car}
              alt="image of a car"
              className="max-h-[600px] xl:max-h-[1200px] max-w-[600px] xl:max-w-[1200px] w-auto mb-28 "
            />
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default Service;
